import React, { useState } from 'react';
import { ConfigProvider, Layout, Menu, Typography, Spin, Drawer, Button } from 'antd';
import {
  EditOutlined, HistoryOutlined, LogoutOutlined, UserOutlined, SettingOutlined, MenuOutlined,
} from '@ant-design/icons';
import { Tag } from 'antd';
import { AuthProvider, useAuth } from './components/AuthContext';
import LoginPage from './pages/LoginPage';
import SurveyPage from './pages/SurveyPage';
import HistoryPage from './pages/HistoryPage';
import AdminPage from './pages/AdminPage';
import './App.css';

const { Header, Content, Footer } = Layout;
const { Title } = Typography;

type PageKey = 'survey' | 'history' | 'admin';

const AppInner: React.FC = () => {
  const { user, loading, logout, isAdmin } = useAuth();
  const [page, setPage] = React.useState<PageKey>('survey');
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '200px auto' }} />;
  }

  if (!user) {
    return <LoginPage />;
  }

  const menuItems = [
    { key: 'survey', icon: <EditOutlined />, label: '测评' },
    { key: 'history', icon: <HistoryOutlined />, label: '历史' },
  ];

  if (isAdmin) {
    menuItems.push({ key: 'admin', icon: <SettingOutlined />, label: '管理' });
  }

  const switchPage = (key: PageKey) => {
    setPage(key);
    setDrawerOpen(false);
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <Header className="app-header">
        {/* Logo + title */}
        <div className="app-header-left">
          <div className="app-logo">
            &#9679;
          </div>
          <Title level={4} className="app-title">幸福之轮</Title>
        </div>

        {/* Desktop nav */}
        <Menu
          mode="horizontal"
          selectedKeys={[page]}
          onClick={({ key }) => setPage(key as PageKey)}
          items={menuItems}
          className="app-nav"
        />

        {/* Desktop user info */}
        <div className="app-header-right">
          <span className="app-user-info">
            <UserOutlined style={{ marginRight: 4 }} />
            {user.displayName || user.username}
            {isAdmin && <Tag color="red" style={{ marginLeft: 6, fontSize: 11 }}>管理员</Tag>}
          </span>
          <span className="app-logout" onClick={logout}>
            <LogoutOutlined /> 退出
          </span>
        </div>

        {/* Mobile hamburger */}
        <Button
          type="text"
          className="app-hamburger"
          icon={<MenuOutlined />}
          onClick={() => setDrawerOpen(true)}
        />
      </Header>

      {/* Mobile nav drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="app-logo" style={{ width: 24, height: 24, fontSize: 14 }}>
              &#9679;
            </div>
            <span>幸福之轮</span>
          </div>
        }
        placement="left"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        styles={{ body: { padding: 0 } }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[page]}
          onClick={({ key }) => switchPage(key as PageKey)}
          items={menuItems}
          style={{ border: 'none' }}
        />
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f0f0f0', marginTop: 8 }}>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
            <UserOutlined style={{ marginRight: 4 }} />
            {user.displayName || user.username}
            {isAdmin && <Tag color="red" style={{ marginLeft: 6, fontSize: 11 }}>管理员</Tag>}
          </div>
          <Button type="text" danger icon={<LogoutOutlined />} onClick={logout} block style={{ textAlign: 'left' }}>
            退出登录
          </Button>
        </div>
      </Drawer>

      <Content className="app-content">
        {page === 'survey' && <SurveyPage />}
        {page === 'history' && <HistoryPage />}
        {page === 'admin' && <AdminPage />}
      </Content>

      <Footer className="app-footer">
        幸福之轮 &copy; {new Date().getFullYear()} — LES Life Enhancement System
      </Footer>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#2ECC71',
          borderRadius: 8,
        },
      }}
    >
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
